'use client';

import React, { useCallback, useMemo, useState } from 'react';
import { useI18n } from 'next-localization';

import { isEmpty } from 'lodash-es';
import { useFormContext } from 'react-hook-form';

import { DisclaimerModal } from '@/components/modals';
import { DISCLAIMER_MODAL_NAME } from '@/components/modals/disclaimer-modal/types';
import { LocationFilter } from '@/features/listing/components/filters/location';
import { useListingContext } from '@/features/listing/hooks/use-listing-context';
import { useBasePath } from '@/hooks/use-base-path';
import { useCustomRouting } from '@/hooks/use-custom-routing';
import { useDistanceContext } from '@/hooks/use-distance-context';
import { usePersistedLocationSearch } from '@/hooks/use-persisted-location-search';
import { useSafeHarborDisclosure } from '@/hooks/use-safe-harbor-disclaimer';
import { LISTING_PAGE } from '@/lib/constants';
import { cn } from '@/lib/utils';
import { useOasApp } from '@/providers/oas-app-provider';
import { NeuButton } from '@hcahealthcaredpx/neutron-react';

import { VALID_SEARCH_REGEX } from '../constants';
import styles from '../landing-search.module.css';
import { AutocompleteQueries } from '../landing-search.types';
import { ProvidersSearch } from './providers-search';
import { SearchBarBottom } from './search-bar-bottom';

export const isSearchValid = (value: string): boolean => VALID_SEARCH_REGEX.test(value);

export const SearchBar = (): JSX.Element => {
  const fields = useOasApp();
  const { t } = useI18n();
  const { withDisclaimer } = useSafeHarborDisclosure();
  const { getSearchedLocation } = usePersistedLocationSearch();
  const { searchValue: value, setSearchValue: setValue, setAppliedFilters } = useListingContext();
  const [isProvidersSearchValid, setProvidersSearchValid] = useState(false);
  const { withBasePath } = useBasePath();

  const { watch } = useFormContext();

  const { zip } = watch();

  const { routerPush } = useCustomRouting();
  const { setLocation } = useDistanceContext();

  const handleSuccesAction = useCallback(() => {
    const searchedLocation = getSearchedLocation();

    if (zip && !isEmpty(searchedLocation)) {
      const { latitude, longitude, address } = searchedLocation;
      setLocation({
        zip,
        latitude,
        longitude,
        address,
      });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const query: any = {};
    if (zip) {
      query.zip = zip;
    }
    if (value?.type === AutocompleteQueries.keywordExact && value?.label) {
      query.keywordExact = value.label;
    }
    if (value?.type === AutocompleteQueries.locationUrlTitle && value?.value) {
      query.locationUrlTitle = value.value;
      query.locationUrlTitleLabel = value.label;
    }
    if (value?.type === AutocompleteQueries.specialtyId && value?.label && value?.value) {
      query.specialty = value.label;
    }

    setAppliedFilters((prev) => ({
      ...prev,
      ...query,
      specialty: value?.type === AutocompleteQueries.specialtyId ? value.label : undefined,
      specialtyId: value?.type === AutocompleteQueries.specialtyId ? value.value : undefined,
    }));

    routerPush({
      pathname: withBasePath(LISTING_PAGE),
      query: query ? query : undefined,
    });
  }, [routerPush, zip, setLocation, value, withBasePath, getSearchedLocation, setAppliedFilters]);
  // Todo: disable btn when we're rerouting to a new page
  const isButtonDisabled = useMemo(
    () => !isProvidersSearchValid && !zip,
    [isProvidersSearchValid, zip]
  );

  return (
    <div className={cn('container', styles.searchBar)}>
      <div
        className={cn('row col-lg-12 d-flex align-items-start my-5 px-3 py-5', styles.neuContainer)}
      >
        <div className="col-lg-5 col-md-6 col-xs-12 d-flex justify-content-center">
          <ProvidersSearch
            label={t('OAS.Listing.Filters.SearchHeader')}
            labelHidden={false}
            tip={t('OAS.LandingPage.ProvidersSearchTip')}
            setValid={setProvidersSearchValid}
            setInputValue={setValue}
            inputValue={value}
            inputReset={() => setValue(undefined)}
            coids={fields.coids}
            program={fields.program}
            locationUrlTitle={fields.locationUrlTitle}
          />
        </div>

        <div className={cn('col-lg-4 col-md-6 col-xs-12 d-flex', styles.useMyLocation)}>
          <LocationFilter removePadding />
        </div>

        <div className={cn('col-lg-3 col-md-6 col-xs-12 d-flex', styles.searchBtn)}>
          <NeuButton
            onClick={() => withDisclaimer(handleSuccesAction, fields.siteType)}
            disabled={isButtonDisabled}
            className="w-100 mt-4"
          >
            {t('OAS.LandingPage.FindADoctorBtn')}
          </NeuButton>
        </div>
        <SearchBarBottom />
      </div>
      <DisclaimerModal
        modalName={DISCLAIMER_MODAL_NAME.SAFE_HARBOR}
        onSuccess={handleSuccesAction}
      />
    </div>
  );
};
